/**
 * O rótulo da linha do tempo do Caso.
 *
 * `detalhe` de `EventoDaLinhaDoTempo` chega com os TOKENS CRUS que o evento
 * gravou no banco (a allowlist do snapshot), não o vocabulário do contrato:
 * o estado vem `"aguardando_operadora"`, não `"AwaitingPayer"`; o item da
 * pendência vem `"endereco"`, não `"Address"`. Por isso este módulo tem seu
 * PRÓPRIO dicionário — reusar `ROTULOS_DE_ESTADO` ou
 * `ROTULOS_DO_ITEM_DA_PENDENCIA` faria todo evento cair no token cru.
 *
 * O gatilho (`de`/`para`/`gatilho` não, só `gatilho`) e a modalidade (`anterior`/`nova`) são
 * as duas exceções: o backend grava o `.ToString()` do enum em inglês, que já
 * é o mesmo vocabulário do contrato — por isso reusam `ROTULOS_DE_GATILHO` e
 * `ROTULOS_DE_MODALIDADE`.
 */

import { ROTULOS_DE_GATILHO, ROTULOS_DE_MODALIDADE, type EventoDaLinhaDoTempo, type GatilhoDoCaso } from './api/tipos';
import { ROTULO_DA_TRILHA } from './captacao/dadosDaCaptacao';

const ROTULO_DO_ESTADO_BRUTO: Record<string, string> = {
  recebido: 'Recebido',
  em_avaliacao: 'Em avaliação',
  orcamento_emitido: 'Orçamento emitido',
  aguardando_operadora: 'Aguardando operadora',
  autorizado: 'Autorizado',
  recusado: 'Recusado',
  encerrado: 'Encerrado',
};

function rotuloDoEstadoBruto(token: string | null | undefined): string {
  if (token == null) return 'estado desconhecido';
  return ROTULO_DO_ESTADO_BRUTO[token] ?? token;
}

function rotuloDaModalidadeBruta(token: string | null | undefined): string {
  if (token == null) return 'nenhuma';
  return Object.hasOwn(ROTULOS_DE_MODALIDADE, token) ? ROTULOS_DE_MODALIDADE[token as 'AD' | 'ID'] : token;
}

function rotuloDoGatilhoBruto(token: string | null | undefined): string {
  if (token == null) return 'gatilho desconhecido';
  return Object.hasOwn(ROTULOS_DE_GATILHO, token) ? ROTULOS_DE_GATILHO[token as GatilhoDoCaso] : token;
}

// O papel de quem agiu (TP-063): o snapshot `caso_transicao.v2` grava o token pt-BR de `usuario.papel`.
const ROTULO_DO_PAPEL_BRUTO: Record<string, string> = {
  operacao: 'Operação',
  contas_medicas: 'Contas médicas',
  coordenacao: 'Coordenação',
};

/** `, como Coordenação` quando o evento diz o papel; vazio no evento gravado antes do TP-063 (sem `papel`). */
function comoPapel(token: string | null | undefined): string {
  if (token == null || token === '') return '';
  return `, como ${ROTULO_DO_PAPEL_BRUTO[token] ?? token}`;
}

const ROTULO_DA_ALCADA_BRUTA: Record<string, string> = {
  coordenacao: 'Coordenação',
  gerencia: 'Gerência',
  diretoria: 'Diretoria',
};

// O grupo da divergência da passagem de plantão (TP-069), como em `passagem/tipos.ts`.
const ROTULO_DO_GRUPO_BRUTO: Record<string, string> = {
  paciente: 'paciente',
  admissao: 'admissão',
  terapias: 'terapias',
  outro: 'outro',
};

// O grupo e, quando o evento traz, o campo que divergiu: "paciente · Carteira". O valor de cada lado nunca vem no evento.
function rotuloDaDivergencia(detalhe: Record<string, string | null>): string {
  const grupo = rotuloBruto(ROTULO_DO_GRUPO_BRUTO, detalhe.grupo);
  return detalhe.campo != null && detalhe.campo !== '' ? `${grupo} · ${detalhe.campo}` : grupo;
}

const ROTULO_DO_ITEM_BRUTO: Record<string, string> = {
  nome: 'Nome do paciente',
  hospital: 'Hospital',
  cpf: 'CPF',
  endereco: 'Endereço',
  vinculo_iw: 'Vínculo com o cadastro do IW',
};

function rotuloDoItemBruto(token: string | null | undefined): string {
  if (token == null) return 'item desconhecido';
  return ROTULO_DO_ITEM_BRUTO[token] ?? token;
}

const ROTULO_DO_TIPO_DE_ESPERA_BRUTO: Record<string, string> = {
  familia: 'Aguardando a família',
  operadora: 'Aguardando a operadora',
};

function rotuloDaFrenteBruta(token: string | null | undefined): string {
  if (token == null) return 'frente desconhecida';
  return (ROTULO_DA_TRILHA as Record<string, string | undefined>)[token] ?? token;
}

function rotuloDoTipoDeEsperaBruto(token: string | null | undefined): string {
  if (token == null) return 'terceiro desconhecido';
  return ROTULO_DO_TIPO_DE_ESPERA_BRUTO[token] ?? token;
}

/** O que uma operação do envio ao IW grava (NR-255). Token fora daqui aparece cru, nunca some. */
const ROTULO_DA_OPERACAO_NO_IW: Readonly<Record<string, string>> = {
  criar_paciente: 'Cadastro do paciente',
  abrir_admissao: 'Abertura da admissão',
  atualizar_admissao: 'Atualização da admissão',
  gravar_orcamento: 'Gravação do orçamento',
  editar_orcamento: 'Edição do orçamento',
};

function rotuloDaOperacaoNoIw(token: string | null | undefined): string {
  if (token == null) return 'Operação desconhecida';
  return ROTULO_DA_OPERACAO_NO_IW[token] ?? token;
}

// O modo da busca que achou o paciente, como o evento `paciente_vinculado_iw` o grava (`TP-053`).
const ROTULO_DO_MODO_DA_BUSCA_BRUTO: Record<string, string> = {
  carteira: 'pela carteira',
  cpf: 'pelo CPF',
  nome_nascimento: 'pelo nome com a data de nascimento',
  nome: 'só pelo nome',
  // NR-258: o paciente nasceu no IW pelo cadastro da plataforma, e o vínculo veio do id que o IW devolveu.
  cadastro: 'pelo cadastro feito na plataforma',
};

function rotuloDoModoDaBuscaBruto(token: string | null | undefined): string {
  if (token == null) return 'modo desconhecido';
  return ROTULO_DO_MODO_DA_BUSCA_BRUTO[token] ?? token;
}

// TP-056: o resultado do retorno chega em token pt-BR (o que o evento gravou); a ação, pelo nome do membro em inglês.
const ROTULO_DO_RESULTADO_BRUTO: Record<string, string> = {
  autorizado_implantacao: 'autorizado a implantação',
  liberada_primeira_diaria: '1ª diária liberada',
  prorrogacao: 'prorrogação',
  pendencia: 'pendência',
  negado: 'negado',
};

const ROTULO_DA_ACAO_BRUTA: Record<string, string> = {
  Authorize: 'caso autorizado',
  Decline: 'caso recusado',
  OpenQuestion: 'vez da Anery aberta',
  RecordOnly: 'só registrado',
};

const ROTULO_DO_MOTIVO_BRUTO: Record<string, string> = {
  leitura_errada: 'o resultado não é o que a carta diz',
  ja_tratado: 'já foi tratado à mão',
  nao_e_retorno: 'não é retorno de autorização',
  outro: 'outro motivo',
};

// TP-070: a área que recebeu o ticket da glosa técnica, como o evento `glosa_tecnica_roteada` a grava.
const ROTULO_DA_AREA_BRUTA: Record<string, string> = {
  relatorio_medico: 'Relatório médico',
  cadastro: 'Cadastro',
  tabela: 'Tabela',
  farmacia: 'Farmácia',
};

function rotuloBruto(dicionario: Record<string, string>, token: string | null | undefined): string {
  if (token == null) return 'desconhecido';
  return dicionario[token] ?? token;
}

/**
 * O texto de um evento da linha do tempo (`S14`). Cobre os tipos que a `US-313` acrescenta e os que a tela já
 * perdeu com a promoção (`TP-023`) e a captação (`TP-040`). Tipo fora deste vocabulário mostra o token cru — a
 * mesma disciplina de `rotuloDoEstado`: aparecer sinalizado, nunca sumir.
 */
function comMotivo(motivo: string | null | undefined): string {
  return motivo == null || motivo.trim() === '' ? '' : `: ${motivo.trim()}`;
}

export function rotuloDoEventoDaLinhaDoTempo(evento: EventoDaLinhaDoTempo): string {
  const detalhe = evento.detalhe;
  switch (evento.tipo) {
    case 'caso_aberto':
      return 'Caso aberto.';
    case 'ticket_aberto':
      return 'Ticket de triagem aberto.';
    case 'email_vinculado':
      return 'E-mail vinculado ao caso.';
    case 'divergencia_thread_codigo':
      return 'Divergência entre a thread e o código do ticket.';
    case 'ticket_promovido_a_caso':
      return 'Ticket de triagem promovido a caso.';
    case 'caso_estado_alterado':
      return `Estado alterado de "${rotuloDoEstadoBruto(detalhe.de)}" para "${rotuloDoEstadoBruto(detalhe.para)}" (${rotuloDoGatilhoBruto(detalhe.gatilho)})${comoPapel(detalhe.papel)}.`;
    case 'caso_modalidade_definida':
      return `Modalidade definida: de "${rotuloDaModalidadeBruta(detalhe.anterior)}" para "${rotuloDaModalidadeBruta(detalhe.nova)}".`;
    case 'pendencia_detectada':
      return `Pendência detectada: ${rotuloDoItemBruto(detalhe.item)}.`;
    case 'pendencia_resolvida':
      return `Pendência resolvida: ${rotuloDoItemBruto(detalhe.item)}.`;
    case 'espera_aberta':
      return `Espera aberta: ${rotuloDoTipoDeEsperaBruto(detalhe.tipo)}.`;
    case 'espera_fechada':
      return `Espera fechada: ${rotuloDoTipoDeEsperaBruto(detalhe.tipo)}.`;
    case 'iw_operacao_solicitada':
      return `${rotuloDaOperacaoNoIw(detalhe.tipo)} enviado ao IW.`;
    case 'iw_operacao_concluida':
      return `O IW gravou o ${rotuloDaOperacaoNoIw(detalhe.tipo).toLowerCase()}${
        detalhe.referenciaIw != null ? ` (id ${detalhe.referenciaIw})` : ''
      }.`;
    case 'iw_operacao_recusada':
      return `O IW não gravou o ${rotuloDaOperacaoNoIw(detalhe.tipo).toLowerCase()} (${
        detalhe.estado === 'esgotada' ? 'o IW não respondeu em nenhuma tentativa' : 'recusado'
      }).`;
    case 'retorno_lido':
      return `Retorno da operadora lido: ${rotuloBruto(ROTULO_DO_RESULTADO_BRUTO, detalhe.resultado)}${detalhe.origem === 'agente' ? ' (da carta)' : ''}.`;
    case 'retorno_confirmado':
      return `Retorno confirmado (${rotuloBruto(ROTULO_DO_RESULTADO_BRUTO, detalhe.resultado)}): ${rotuloBruto(ROTULO_DA_ACAO_BRUTA, detalhe.acao)}.`;
    case 'retorno_descartado':
      return `Retorno descartado (${rotuloBruto(ROTULO_DO_RESULTADO_BRUTO, detalhe.resultado)}): ${rotuloBruto(ROTULO_DO_MOTIVO_BRUTO, detalhe.motivo)}.`;
    case 'questionamento_aberto':
      return 'Pendência da operadora: a vez é da Anery.';
    case 'questionamento_respondido':
      return 'Pendência respondida: a vez da Anery fechou.';
    case 'frente_avancada':
    case 'frente_concluida': {
      const passo = `Passo ${detalhe.passo ?? '?'} de ${rotuloDaFrenteBruta(detalhe.frente)} fechado`;
      const fim = evento.tipo === 'frente_concluida' ? '; a frente concluiu' : '';
      // `job` é o sistema: o passo de conferência que já estava todo provado fecha sozinho (TP-058, D18).
      return evento.atorTipo === 'job'
        ? `${passo} pelo sistema: tudo o que ele pede já estava provado${fim}.`
        : `${passo}${fim}.`;
    }
    // A origem da prova (TP-061, D5): quem fez aparece no ator; aqui, o que foi feito e com que item.
    case 'documento_registrado': {
      if (detalhe.origem === 'email') return 'Documento chegou pelo e-mail.';
      if (detalhe.origem === 'iw') return 'Documento veio do IW.';
      return detalhe.reenviado === 'true' ? 'Documento reenviado pela tela.' : 'Documento enviado pela tela.';
    }
    case 'documento_recebido':
      return `Documento atribuído a ${detalhe.rotulo ?? 'um item'}.`;
    case 'documento_validado':
      return `${detalhe.rotulo ?? 'Documento'} validado.`;
    case 'documento_reaberto':
      return `${detalhe.rotulo ?? 'Documento'} reaberto${comMotivo(detalhe.motivo)}.`;
    case 'documento_dispensado':
      return `${detalhe.rotulo ?? 'Documento'} dispensado${comMotivo(detalhe.motivo)}.`;
    case 'item_aceito_sem_prova':
      return `${detalhe.rotulo ?? detalhe.chave ?? 'Item'} aceito sem prova${comMotivo(detalhe.motivo)}.`;
    case 'glosa_tecnica_roteada':
      return `Glosa técnica roteada para a área ${rotuloBruto(ROTULO_DA_AREA_BRUTA, detalhe.area)}.`;
    case 'glosa_tecnica_cancelada':
      return `Ticket da glosa técnica cancelado (área ${rotuloBruto(ROTULO_DA_AREA_BRUTA, detalhe.area)}): o tipo da glosa mudou.`;
    case 'paciente_vinculado_iw':
      return `Paciente vinculado ao cadastro do IW (${rotuloDoModoDaBuscaBruto(detalhe.modo)}).`;
    case 'paciente_desvinculado_iw':
      return 'Vínculo com o cadastro do IW desfeito.';
    case 'paciente_novo_no_iw_marcado':
      return 'Marcado como paciente novo, sem cadastro no IW.';
    // Quem aprovou a margem (TP-064). A margem não vem: é dado de alçada, fica no bloco da aba Orçamento.
    case 'alcada_registrada': {
      const aprovadora = ROTULO_DA_ALCADA_BRUTA[detalhe.aprovadora ?? ''] ?? detalhe.aprovadora ?? '?';
      const sugerida = detalhe.sugerida ? `sugerida ${ROTULO_DA_ALCADA_BRUTA[detalhe.sugerida] ?? detalhe.sugerida}` : 'sem alçada sugerida';
      return `Margem aprovada pela ${aprovadora} (${sugerida})${comMotivo(detalhe.motivo)}.`;
    }
    // A passagem de plantão (TP-069). O texto da divergência não vem: fica no bloco, o evento diz só o grupo.
    case 'passagem_de_plantao_registrada': {
      if (detalhe.conferencia !== 'com_divergencia') return 'Passagem de plantão registrada, sem divergência.';
      const n = detalhe.divergencias ?? '?';
      return `Passagem de plantão registrada, com ${n} ${n === '1' ? 'divergência' : 'divergências'}.`;
    }
    case 'passagem_de_plantao_validada':
      return 'Passagem de plantão validada.';
    case 'divergencia_da_passagem_aberta':
      return `Divergência na passagem (${rotuloDaDivergencia(detalhe)}): pedido de ajuste à captação.`;
    case 'divergencia_da_passagem_corrigida':
      return `Guia corrigida na divergência da passagem (${rotuloDaDivergencia(detalhe)}).`;
    default:
      return evento.tipo === '' ? 'Evento sem tipo' : evento.tipo;
  }
}
